import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import Collapse from '@mui/material/Collapse';

import DeleteIcon from '@mui/icons-material/Delete';
import ElectricMeterRoundedIcon from '@mui/icons-material/ElectricMeterRounded';
import GasMeterRoundedIcon from '@mui/icons-material/GasMeterRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; // default billing icon
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// --- helpers reused here too ---
const SEVERITY = ['Unknown', 'Success', 'Warning', 'Error', 'Critical'];
const norm = (s) => {
  if (!s || typeof s !== 'string') return 'Unknown';
  const x = s.trim().toLowerCase();
  if (x.startsWith('succ')) return 'Success';
  if (x.startsWith('warn')) return 'Warning';
  if (x === 'error' || x === 'err') return 'Error';
  if (x.startsWith('crit')) return 'Critical';
  return 'Unknown';
};
const worst = (arr) =>
  arr.reduce(
    (w, s) => (SEVERITY.indexOf(norm(s)) > SEVERITY.indexOf(w) ? norm(s) : w),
    'Unknown',
  );

// icon per DataInputName (tweak rules as you like)
const IconForDataInput = ({ name }) => {
  if (/electric|kwh|energy/i.test(name)) return <ElectricMeterRoundedIcon />;
  if (/gas/i.test(name)) return <GasMeterRoundedIcon />;
  if (/water|flow|m3/i.test(name)) return <WaterDropRoundedIcon />;
  if (/heat|thermal/i.test(name))
    return (
      <MenuRoundedIcon sx={{ transform: 'rotate(90deg)', opacity: 0.6 }} />
    );
  return <ReceiptLongIcon />; // default billing icon
};

// Try to associate a BillingMeterHealthCheck item with a specific billing meter
const matchCheckToMeter = (check, meter) => {
  if (!check || !meter) return false;

  const unitId = meter?.unit?.UnitId;
  if (!unitId) return false;

  // Top-level DeviceIds on the check
  const checkHasUnitId =
    Array.isArray(check.DeviceIds) && check.DeviceIds.includes(unitId);

  // Nested DataInputHealthChecks DeviceIds
  const subHasUnitId = (check.DataInputHealthChecks || []).some((sc) => {
    const scIds = sc?.DeviceIds || [];
    return Array.isArray(scIds) && scIds.includes(unitId);
  });

  return checkHasUnitId || subHasUnitId;
};

// Build a per-meter health view: matched checks, worst status, flattened messages
const getMeterHealth = (meter, billingHC) => {
  if (!billingHC) return null;
  const allChecks = billingHC.BillingMeterHealthChecks || [];
  const matched = allChecks.filter((chk) => matchCheckToMeter(chk, meter));
  if (matched.length === 0) return null;

  const statuses = [
    ...matched.map((c) => c?.DataInputStatus).filter(Boolean),
    ...matched
      .flatMap((c) => (c?.DataInputHealthChecks || []).map((sc) => sc?.Status))
      .filter(Boolean),
  ];
  const status = statuses.length ? worst(statuses) : 'Unknown';
  const messages = matched.flatMap((c) =>
    (c?.DataInputHealthChecks || []).flatMap((sc) => messagesFromSub(sc)),
  );
  return { status, matchedChecks: matched, messages };
};

const statusVisual = (status) => {
  const s = norm(status);
  switch (s) {
    case 'Success':
      return { key: 'success', Icon: CheckCircleRoundedIcon };
    case 'Warning':
      return { key: 'warning', Icon: WarningRoundedIcon };
    case 'Error':
      return { key: 'error', Icon: ErrorRoundedIcon };
    case 'Critical':
      return { key: 'error', Icon: PriorityHighRoundedIcon };
    default:
      return { key: 'default', Icon: HelpOutlineRoundedIcon };
  }
};

// Gathers messages (strings) from a billing sub-check
const messagesFromSub = (sub) => [
  ...(sub?.Warnings || []).map((w) => w.message),
  ...(sub?.Errors || []).map((e) => e.message),
];

function BillingMeterHealthSection({
  billing, // the BillingMeterHealthCheck object
  hideSuccess = true, // don't show all-green checks
  spacing = 1,
  size = 'small',
}) {
  const [openIdx, setOpenIdx] = React.useState(null);

  if (!billing) return null;

  // build per-check data from BillingMeterHealthChecks
  const checks = (billing.BillingMeterHealthChecks || []).map((chk, idx) => {
    const subStatuses = (chk.DataInputHealthChecks || []).map(
      (sc) => sc?.Status,
    );
    const overall = worst(
      [chk.DataInputStatus, ...subStatuses].filter(Boolean),
    );
    const vis = statusVisual(overall);

    // collect all messages for this check (flattened)
    const messages = (chk.DataInputHealthChecks || []).flatMap((sc) =>
      messagesFromSub(sc),
    );
    return {
      idx,
      name: chk.DataInputName,
      status: overall,
      vis,
      messages,
      raw: chk,
    };
  });

  const visible = hideSuccess
    ? checks.filter((c) => c.status !== 'Success')
    : checks;
  if (visible.length === 0) return null;

  return (
    <>
      <Stack
        direction="row"
        spacing={spacing}
        alignItems="center"
        sx={{ mb: 1 }}
      >
        {visible.map((c) => {
          const StatusIcon = c.vis.Icon;
          return (
            <Badge
              key={c.idx}
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <StatusIcon
                  sx={{
                    fontSize: 14,
                    color: (theme) =>
                      c.vis.key === 'default'
                        ? theme.palette.text.disabled
                        : theme.palette[c.vis.key].main,
                  }}
                />
              }
            >
              <IconButton
                size={size}
                onClick={() => setOpenIdx(c.idx)}
                sx={{ border: 1, borderColor: 'divider' }}
                aria-label={c.name}
              >
                <IconForDataInput name={c.name} />
              </IconButton>
            </Badge>
          );
        })}
      </Stack>

      {visible.map((c) => {
        const open = openIdx === c.idx;
        return (
          <Dialog
            key={c.idx}
            open={open}
            onClose={() => setOpenIdx(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                {React.createElement(c.vis.Icon, {
                  sx: (theme) => ({
                    color:
                      c.vis.key === 'default'
                        ? theme.palette.text.disabled
                        : theme.palette[c.vis.key].main,
                  }),
                })}
                <Typography variant="subtitle1" component="div">
                  {c.name} ({c.status})
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              {c.messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No messages.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {c.messages.map((m, i) => (
                    <Box key={i} sx={{ fontSize: 14, lineHeight: 1.35 }}>
                      • {m}
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenIdx(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })}
    </>
  );
}

const getIconForBillingMeterType = (type) => {
  switch (type) {
    case 'HeatMeter':
      return (
        <MenuRoundedIcon sx={{ transform: 'rotate(90deg)', opacity: '50%' }} />
      );
    case 'GasMeter':
    case 'WaterMeter':
      return <GasMeterRoundedIcon />;
    case 'ElectricityMeter':
      return <ElectricMeterRoundedIcon />;
    default:
      return null;
  }
};

const billingMeterTypes = [
  'HeatMeter',
  'GasMeter',
  'WaterMeter',
  'ElectricityMeter',
];

const EditBuildingBillingMeterDialog = ({
  open,
  onClose,
  units,
  billingMeters,
  billingMeterHealthCheck,
  handleAddBillingMeter,
  handleDeleteBillingMeter,
}) => {
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedUnitTypeInputId, setSelectedUnitTypeInputId] = useState(null);
  const [selectedUnitTypeInput, setSelectedUnitTypeInput] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState(null);
  const [selectedBillingMeterType, setSelectedBillingMeterType] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const billingMeterUnits = billingMeters.map((meter) => {
    const unit = units?.find((u) => u.UnitId === meter.UnitId);
    return {
      ...meter, // keep the meter info
      unit, // attach the full unit object (or undefined if not found)
    };
  });

  const handleUnitTypeInputChange = (newValue) => {
    if (newValue) {
      setSelectedUnitTypeInput(newValue); // Set the entire object, not just the label
      setSelectedUnitTypeInputId(newValue.UnitTypeInputId);
    } else {
      setSelectedUnitTypeInputId(null);
      setSelectedUnitTypeInput(null);
      setSelectedDataType(null);
    }
  };

  const handleDataTypeChange = (newValue) => {
    setSelectedDataType(newValue);
  };

  const handleClose = () => {
    setSelectedOption(null);
    setSelectedUnitTypeInputId(null);
    setSelectedUnitTypeInput(null);
    setSelectedDataType(null);
    setSelectedBillingMeterType('');
    setIsAddFormOpen(false);
    onClose();
  };

  // Filter out units that have already been selected in the billing meters list
  const filteredOptions = units?.filter(
    (option) =>
      !billingMeterUnits.some((meter) => meter.UnitId === option.UnitId),
  );

  if (!billingMeterUnits) return;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>Billingmeters</DialogTitle>
      <DialogContent dividers>
        {billingMeterUnits.map((billingMeter) => {
          const id = billingMeter.BillingMeterId;
          const mh = getMeterHealth(billingMeter, billingMeterHealthCheck); // may be null
          const sev = mh?.status || 'Unknown';
          const { key: colorKey, Icon: SevIcon } = statusVisual(sev);
          const showBadge = mh; // hide on success

          const isOpen = expanded.has(id);

          return (
            <Box key={id} sx={{ mb: 1 }} component={Card} variant="outlined">
              <ListItem
                secondaryAction={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        handleDeleteBillingMeter(id);
                        handleClose();
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                }
                sx={{ cursor: mh ? 'pointer' : 'default' }}
                onClick={() => mh && toggleExpanded(id)}
              >
                <ListItemIcon sx={{ border: '1px solid divider' }}>
                  {showBadge ? (
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <SevIcon
                          sx={{
                            fontSize: 14,
                            color: (theme) =>
                              colorKey === 'default'
                                ? theme.palette.text.disabled
                                : theme.palette[colorKey].main,
                          }}
                        />
                      }
                    >
                      {getIconForBillingMeterType(
                        billingMeter.BillingMeterType,
                      )}
                    </Badge>
                  ) : (
                    getIconForBillingMeterType(billingMeter.BillingMeterType)
                  )}
                </ListItemIcon>

                <Stack display="flex" flexDirection="row" flexGrow={1}>
                  <ListItemText
                    primary="Type"
                    primaryTypographyProps={{ variant: 'caption' }}
                    secondary={billingMeter.BillingMeterType}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  <ListItemText
                    primary="Name"
                    primaryTypographyProps={{ variant: 'caption' }}
                    secondary={billingMeter?.unit?.Name || 'N/A'}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  <ListItemText
                    primary="Unit ID"
                    primaryTypographyProps={{ variant: 'caption' }}
                    secondary={billingMeter?.unit?.UnitId}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  <ListItemText
                    primary="Unit Type Input ID"
                    primaryTypographyProps={{ variant: 'caption' }}
                    secondary={billingMeter?.UnitTypeInputId}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  <ListItemText
                    primary="Data Type"
                    primaryTypographyProps={{ variant: 'caption' }}
                    secondary={billingMeter?.DataType}
                  />
                </Stack>
              </ListItem>

              {/* Inline details */}
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Divider />
                <Box sx={{ p: 2, pt: 1 }}>
                  {!mh || mh.matchedChecks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No health details for this meter.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {mh.matchedChecks.map((chk, i) => {
                        const chkStatus = chk?.DataInputStatus || 'Unknown';
                        const { key: chkColorKey, Icon: ChkIcon } =
                          statusVisual(chkStatus);
                        return (
                          <Box key={i}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ mb: 0.5 }}
                            >
                              <ChkIcon
                                sx={(theme) => ({
                                  fontSize: 18,
                                  color:
                                    chkColorKey === 'default'
                                      ? theme.palette.text.disabled
                                      : theme.palette[chkColorKey].main,
                                })}
                              />
                              <Typography variant="subtitle2">
                                {chk.DataInputName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ opacity: 0.7 }}
                              >
                                ({chkStatus})
                              </Typography>
                            </Stack>

                            {/* sub-checks + messages */}
                            <Stack spacing={0.75} sx={{ pl: 3 }}>
                              {(chk.DataInputHealthChecks || []).map(
                                (sc, j) => {
                                  const scStatus = sc?.Status || 'Unknown';
                                  const { key: scColorKey, Icon: ScIcon } =
                                    statusVisual(scStatus);
                                  const lines = messagesFromSub(sc);
                                  return (
                                    <Box key={j}>
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                      >
                                        <ScIcon
                                          sx={(theme) => ({
                                            fontSize: 16,
                                            color:
                                              scColorKey === 'default'
                                                ? theme.palette.text.disabled
                                                : theme.palette[scColorKey]
                                                    .main,
                                          })}
                                        />
                                        <Typography variant="body2">
                                          {sc.Name}{' '}
                                          <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{ opacity: 0.7 }}
                                          >
                                            ({scStatus})
                                          </Typography>
                                        </Typography>
                                      </Stack>
                                      {lines.length === 0 ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ pl: 3 }}
                                        >
                                          No messages.
                                        </Typography>
                                      ) : (
                                        <Box
                                          sx={{
                                            pl: 3,
                                            display: 'grid',
                                            gap: 0.5,
                                          }}
                                        >
                                          {lines.map((m, k) => (
                                            <Typography key={k} variant="body2">
                                              • {m}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                },
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        })}

        {billingMeters.length === 0 && !isAddFormOpen && (
          <Box sx={{ padding: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No billing meters found.
            </Typography>
          </Box>
        )}

        {isAddFormOpen && (
          <Stack spacing={2}>
            <Autocomplete
              options={billingMeterTypes}
              getOptionLabel={(option) => option}
              onChange={(event, newValue) =>
                setSelectedBillingMeterType(newValue)
              }
              value={selectedBillingMeterType}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Meter Type"
                  variant="filled"
                />
              )}
            />
            <Autocomplete
              value={selectedOption}
              onChange={(event, newValue) => setSelectedOption(newValue)}
              options={filteredOptions}
              getOptionLabel={(option) => option.Name}
              renderOption={(props, option) => (
                <li {...props}>{option.Name}</li>
              )}
              renderInput={(params) => (
                <TextField
                  variant="filled"
                  {...params}
                  label="Select an Option"
                />
              )}
              fullWidth
            />
            {selectedOption && (
              <>
                <Autocomplete
                  options={selectedOption.UnitSensors.map((sensor) => ({
                    label:
                      sensor.CustomName ||
                      `${sensor.UnitTypeInput.Name} (No Custom Name)`,
                    UnitTypeInputId: sensor.UnitTypeInput.UnitTypeInputId,
                    DataTypes: selectedOption.RecordedDataTypes,
                  }))}
                  getOptionLabel={(option) =>
                    `${option.label} ${option.UnitTypeInputId}` || ''
                  }
                  onChange={(event, newValue) =>
                    handleUnitTypeInputChange(newValue)
                  }
                  value={selectedUnitTypeInput || null} // Make sure to use the entire object
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Unit Type Input"
                      variant="filled"
                    />
                  )}
                />
                {selectedUnitTypeInput && (
                  <Autocomplete
                    options={
                      selectedUnitTypeInput?.DataTypes?.flatMap(
                        (dataType) =>
                          dataType.UnitTypeInput.UnitTypeInputId ===
                          selectedUnitTypeInputId
                            ? dataType.DataTypes
                            : [], // Return an empty array for non-matching UnitTypeInputId
                      ) || [] // Fallback to an empty array if selectedUnitTypeInput or DataTypes is undefined
                    }
                    getOptionLabel={(option) => option || 'Unknown'}
                    onChange={(event, newValue) =>
                      handleDataTypeChange(newValue)
                    }
                    value={selectedDataType || null} // Ensure the value is valid or set to null
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Data Type"
                        variant="filled"
                      />
                    )}
                  />
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        {!isAddFormOpen && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsAddFormOpen(true)}
          >
            Add Billing Meter
          </Button>
        )}

        {isAddFormOpen && (
          <Button
            disabled={
              !selectedOption || !selectedUnitTypeInputId || !selectedDataType
            }
            onClick={() => {
              if (
                selectedOption &&
                selectedBillingMeterType &&
                selectedUnitTypeInput
              ) {
                handleAddBillingMeter({
                  UnitId: selectedOption.UnitId,
                  UnitTypeInputId: selectedUnitTypeInput.UnitTypeInputId,
                  DataType: selectedDataType,
                  BillingMeterType: selectedBillingMeterType,
                });
                handleClose();
              }
            }}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditBuildingBillingMeterDialog;
